import { useCallback } from 'react'
import type { SelectionFetchState } from './use-selection-fetch'
import type { PendingSyncControl } from './use-pending-sync'

function addToSet(prev: Set<string>, id: string): Set<string> {
  if (prev.has(id)) return prev
  const next = new Set(prev)
  next.add(id)
  return next
}

function removeFromSet(prev: Set<string>, id: string): Set<string> {
  if (!prev.has(id)) return prev
  const next = new Set(prev)
  next.delete(id)
  return next
}

export interface SelectionMutations {
  isSelected: (id: string) => boolean
  select: (id: string) => void
  deselect: (id: string) => void
  toggle: (id: string) => void
  selectMany: (ids: string[]) => void
  deselectMany: (ids: string[]) => void
  selectRange: (ids: string[]) => void
}

export function useSelectionMutations(
  fetchState: SelectionFetchState,
  pendingSync: PendingSyncControl
): SelectionMutations {
  const { selectedIds, setSelectedIds } = fetchState
  const { pendingChanges, scheduleSync } = pendingSync

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds])

  const select = useCallback(
    (id: string) => {
      setSelectedIds((prev) => addToSet(prev, id))
      pendingChanges.current.add.add(id)
      pendingChanges.current.remove.delete(id)
      scheduleSync()
    },
    [setSelectedIds, pendingChanges, scheduleSync]
  )

  const deselect = useCallback(
    (id: string) => {
      setSelectedIds((prev) => removeFromSet(prev, id))
      pendingChanges.current.remove.add(id)
      pendingChanges.current.add.delete(id)
      scheduleSync()
    },
    [setSelectedIds, pendingChanges, scheduleSync]
  )

  const toggle = useCallback(
    (id: string) => {
      if (selectedIds.has(id)) deselect(id)
      else select(id)
    },
    [selectedIds, select, deselect]
  )

  const selectMany = useCallback(
    (ids: string[]) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) {
          next.add(id)
          pendingChanges.current.add.add(id)
          pendingChanges.current.remove.delete(id)
        }
        return next
      })
      scheduleSync()
    },
    [setSelectedIds, pendingChanges, scheduleSync]
  )

  const deselectMany = useCallback(
    (ids: string[]) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) {
          next.delete(id)
          pendingChanges.current.remove.add(id)
          pendingChanges.current.add.delete(id)
        }
        return next
      })
      scheduleSync()
    },
    [setSelectedIds, pendingChanges, scheduleSync]
  )

  const selectRange = useCallback(
    (ids: string[]) => {
      selectMany(ids)
    },
    [selectMany]
  )

  return { isSelected, select, deselect, toggle, selectMany, deselectMany, selectRange }
}
